import * as React from 'react';

import {emotionStyled} from '@packages/utilities/styled';
import {useListItem, useMergeRefs} from '@floating-ui/react';

import {Button} from '../../Button';
import {IconButton, type IconButtonProps} from '../../IconButton';
import {Slot} from '../../Slot';
import {ChevronSmallDownIcon} from '../../../icons';
import {mergeProps} from '../../../utils/mergeProps';

import {MenuTriggerProps} from '../_types';
import {MenuItemWrapper} from '../MenuItem';
import {useMenuContext} from '../useMenu';

const ButtonTriggerView = emotionStyled(Button)(
  {},
  {
    variants: {
      hasDownArrow: {
        true: {
          paddingRight: '$1',
        },
      },
    },
  }
);

const NestedMenuTrigger = React.forwardRef<HTMLElement, MenuTriggerProps>(
  ({as, asChild, prefix, suffix, children, ...props}, forwardedRef) => {
    const {getReferenceProps, parent} = useMenuContext();
    const item = useListItem();

    const mergedRefs = useMergeRefs([item.ref, forwardedRef]);

    const referenceProps = getReferenceProps(parent.getItemProps());
    const mergedProps = mergeProps(referenceProps, props);

    return (
      <MenuItemWrapper
        as={as}
        asChild={asChild}
        isNested
        prefix={prefix}
        ref={mergedRefs}
        suffix={suffix}
        tabIndex={parent.activeIndex === item.index ? 0 : -1}
        {...mergedProps}
      >
        {children}
      </MenuItemWrapper>
    );
  }
);

/**
 * @spring-must-be-inside Menu
 * @spring-constraint-severity error
 */
export const MenuTrigger = React.forwardRef<
  HTMLButtonElement,
  MenuTriggerProps
>(
  (
    {
      as,
      asChild,
      children,
      disabled,
      hasDownArrow,
      isDisabled: isDisabledProp,
      prefix,
      suffix,
      ...props
    },
    forwardedRef
  ) => {
    const {
      getReferenceProps,
      isNested,
      parent,
      refs,
      setIsTriggerDisabled,
      triggerRef,
    } = useMenuContext();

    const isDisabled = Boolean(isDisabledProp || disabled);

    React.useEffect(() => {
      setIsTriggerDisabled(isDisabled);
    }, [isDisabled, setIsTriggerDisabled]);

    const mergedRefs = useMergeRefs([
      refs.setReference,
      triggerRef,
      forwardedRef,
    ]);

    const referenceProps = getReferenceProps(parent.getItemProps());
    const mergedProps = mergeProps(referenceProps, props);

    const triggerProps = {
      isDisabled,
      ...mergedProps,
    };

    return (
      <>
        {isNested ? (
          <NestedMenuTrigger
            as={as}
            asChild={asChild}
            prefix={prefix}
            ref={mergedRefs}
            suffix={suffix}
            {...triggerProps}
          >
            {children}
          </NestedMenuTrigger>
        ) : (
          <>
            {asChild ? (
              <Slot {...triggerProps} ref={mergedRefs}>
                {children}
              </Slot>
            ) : (
              <ButtonTriggerView
                as={as}
                hasDownArrow={hasDownArrow}
                prefix={prefix}
                ref={mergedRefs}
                shouldEdgeAlignIcons={hasDownArrow}
                suffix={
                  hasDownArrow ? (
                    <ChevronSmallDownIcon
                      aria-hidden
                      data-automation-id="down-arrow"
                    />
                  ) : (
                    suffix
                  )
                }
                {...triggerProps}
              >
                {children}
              </ButtonTriggerView>
            )}
          </>
        )}
      </>
    );
  }
);

/**
 * @spring-must-be-inside Menu
 * @spring-constraint-severity error
 */
export const IconMenuTrigger = React.forwardRef<
  HTMLButtonElement,
  IconButtonProps
>(({...props}: IconButtonProps, ref) => {
  const {isDisabled} = props;
  // Note: `tabIndex=-1` is being set somewhere when isDisabled=true, so setting it back to 0
  // if a tooltip exists and it's disabled
  return (
    <MenuTrigger asChild isDisabled={isDisabled} ref={ref}>
      <IconButton
        {...props}
        {...(props.withTooltip && isDisabled && {tabIndex: 0})}
      />
    </MenuTrigger>
  );
});
